import { useState } from 'react'
import { FileX, MapPin, Building2, Users, ReceiptText, Coins } from 'lucide-react'
import {
  Bar, BarChart, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'

// ── Tipos ──────────────────────────────────────────────────────────────────

interface GrupoItem {
  nombre: string
  facturas: number
  monto: number
  puntos: number
}

interface Factura {
  numero: string
  fecha_emision: string
  estado: string
  establecimiento: string
  razon_social: string
  ciudad: string
  provincia: string
  representante: string
  monto: number
  puntos: number
}

interface ExcluidasData {
  periodo: string
  total_facturas: number
  total_monto: number
  total_puntos: number
  por_representante: GrupoItem[]
  por_provincia: GrupoItem[]
  por_ciudad: GrupoItem[]
  por_razon_social: GrupoItem[]
  facturas: Factura[]
}

// ── Constantes ──────────────────────────────────────────────────────────────

const BLUE = '#6a5cbc'
const BLUE_LIGHT = '#6a5cbc18'
const GREEN = '#2fbc80'
const SHADES = ['#6a5cbc', '#7d71c9', '#9186d6', '#a49be3', '#c4bef0']

type Metrica = 'facturas' | 'monto' | 'puntos'

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number, decimals = 0): string {
  return n.toLocaleString('es-PE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

function shade(idx: number, total: number): string {
  const ratio = total <= 1 ? 0 : idx / (total - 1)
  const i = Math.round(ratio * (SHADES.length - 1))
  return SHADES[i]
}

// ── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label, value, sub, icon: Icon,
}: {
  label: string; value: string; sub?: string; icon: React.ElementType
}) {
  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-card h-full"
      style={{ backgroundImage: `linear-gradient(to right, ${BLUE_LIGHT}, transparent 60%)` }}
    >
      <div className="h-full px-4 py-4 flex flex-col justify-center gap-2">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        <div className="flex items-center gap-3">
          <div className="shrink-0">
            <Icon className="h-9 w-9" style={{ color: BLUE }} />
          </div>
          <div className="flex-1 min-w-0 text-right">
            <div className="text-3xl font-bold text-foreground leading-tight">{value}</div>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Tooltip custom ────────────────────────────────────────────────────────────

function GrupoTooltip({ active, payload, label, metrica }: {
  active?: boolean
  payload?: { value: number }[]
  label?: string
  metrica: Metrica
}) {
  if (!active || !payload?.length) return null
  const v = payload[0].value
  const display =
    metrica === 'monto' ? `S/ ${fmt(v, 2)}` :
    metrica === 'puntos' ? fmt(v) :
    `${fmt(v)} facturas`
  return (
    <div className="bg-white dark:bg-card border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow text-sm">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      <p className="text-muted-foreground">{display}</p>
    </div>
  )
}

// ── Gráfica de grupo ──────────────────────────────────────────────────────────

function GrupoChart({
  title, data, metrica, icon: Icon,
}: {
  title: string
  data: GrupoItem[]
  metrica: Metrica
  icon: React.ElementType
}) {
  const sorted = [...data].sort((a, b) => b[metrica] - a[metrica])
  const barHeight = 44
  const chartHeight = Math.max(sorted.length * barHeight + 20, 120)
  const formatLabel = (v: number) =>
    metrica === 'monto' ? `S/ ${fmt(v, 0)}` :
    metrica === 'puntos' ? `${fmt(v)} pts` :
    `${fmt(v)}`

  return (
    <Card className="border border-gray-200 dark:border-gray-700">
      <CardHeader className="pb-2 flex flex-row items-center gap-2 space-y-0">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={sorted} layout="vertical" margin={{ left: 0, right: 90, top: 4, bottom: 4 }}>
            <XAxis type="number" hide />
            <YAxis
              dataKey="nombre"
              type="category"
              tickLine={false}
              axisLine={false}
              width={160}
              tick={{ fontSize: 10 }}
              interval={0}
            />
            <Tooltip content={<GrupoTooltip metrica={metrica} />} cursor={{ fill: `${BLUE}10` }} />
            <Bar dataKey={metrica} radius={[0, 4, 4, 0]}>
              {sorted.map((_, idx) => (
                <Cell key={idx} fill={shade(idx, sorted.length)} />
              ))}
              <LabelList
                dataKey={metrica}
                position="right"
                style={{ fontSize: 11, fill: '#6b7280' }}
                formatter={(v: number) => formatLabel(v)}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

// ── Página principal ─────────────────────────────────────────────────────────

export function MediafarmaPeruExcluidasPage() {
  useDocumentTitle('Facturas Excluidas · Medifarma Perú')

  const { data, isLoading, isError } = useDashReport<ExcluidasData>('medifarma-peru/facturas-excluidas')
  const [metrica, setMetrica] = useState<Metrica>('facturas')

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BLUE }} />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">
        Error al cargar los datos.
      </div>
    )
  }

  const metricas: { key: Metrica; label: string }[] = [
    { key: 'facturas', label: 'Facturas' },
    { key: 'monto', label: 'Monto S./' },
    { key: 'puntos', label: 'Puntos' },
  ]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">

      {/* ── Header ── */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="py-4 px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-12 w-auto dark:invert" />
              <div className="border-l border-gray-200 dark:border-gray-700 pl-5">
                <h1 className="text-xl font-bold text-foreground leading-tight">
                  Facturas Excluidas — Establecimientos No Participantes
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {data.periodo} · Programa de Fidelización Medifarma Perú
                </p>
              </div>
            </div>
            <img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQxC1wKpIiaTpYQ9UIOCvWwoUZDLGpSxWvaR5x1cEGXyYaSxkCDU6SkRHDZ&s=10"
              alt="Medifarma Peru"
              className="h-12 w-auto flex-shrink-0 object-contain"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Contexto ── */}
      <Card className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-card">
        <CardContent className="py-4 px-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Facturas subidas por dependientes afiliados a{' '}
            <span className="font-semibold text-foreground">establecimientos no incluidos</span>{' '}
            en el programa. Estas facturas fueron <span className="font-semibold text-foreground">rechazadas</span> y
            no generan puntos válidos. El análisis permite identificar patrones de farmacias fuera del programa
            y evaluar su potencial incorporación.
          </p>
        </CardContent>
      </Card>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          icon={FileX}
          label="Total facturas excluidas"
          value={fmt(data.total_facturas)}
          sub={data.periodo}
        />
        <KpiCard
          icon={ReceiptText}
          label="Monto total"
          value={`S/ ${fmt(data.total_monto, 2)}`}
          sub="Ventas potenciales excluidas"
        />
        <KpiCard
          icon={Coins}
          label="Puntos totales"
          value={fmt(data.total_puntos)}
          sub="Puntos rechazados"
        />
      </div>

      {/* ── Selector de métrica ── */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide mr-1">Ver por:</span>
        {metricas.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setMetrica(key)}
            className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
            style={
              metrica === key
                ? { backgroundColor: BLUE, color: '#fff' }
                : { backgroundColor: `${BLUE}14`, color: BLUE }
            }
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Gráficas 2×2 ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GrupoChart
          title="Por representante"
          data={data.por_representante}
          metrica={metrica}
          icon={Users}
        />
        <GrupoChart
          title="Por razón social"
          data={data.por_razon_social}
          metrica={metrica}
          icon={Building2}
        />
        <GrupoChart
          title="Por provincia"
          data={data.por_provincia}
          metrica={metrica}
          icon={MapPin}
        />
        <GrupoChart
          title="Por ciudad / distrito"
          data={data.por_ciudad}
          metrica={metrica}
          icon={MapPin}
        />
      </div>

      {/* ── Tabla de detalle ── */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Detalle de facturas excluidas
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">N° Comprobante</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Fecha</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Razón Social</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ciudad</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Representante</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Monto S./</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Puntos</th>
                </tr>
              </thead>
              <tbody>
                {data.facturas.map((f) => (
                  <tr
                    key={f.numero}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
                  >
                    <td className="px-4 py-2.5 font-mono text-xs text-foreground">{f.numero}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{f.fecha_emision}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground">{f.razon_social}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{f.ciudad}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground">{f.representante.split(' ').slice(0, 2).join(' ')}</td>
                    <td className="px-4 py-2.5 text-xs text-right font-medium text-foreground">
                      {fmt(f.monto, 2)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-right" style={{ color: GREEN }}>
                      {fmt(f.puntos)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 font-semibold">
                  <td colSpan={5} className="px-4 py-2.5 text-xs text-muted-foreground uppercase tracking-wide">
                    Total — {fmt(data.total_facturas)} facturas
                  </td>
                  <td className="px-4 py-2.5 text-xs text-right text-foreground">
                    {fmt(data.total_monto, 2)}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-right" style={{ color: GREEN }}>
                    {fmt(data.total_puntos)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <p className="text-center text-xs text-muted-foreground/40 pb-2">
        Tonal Strategies · {data.periodo} · Establecimientos no participantes
      </p>
    </div>
  )
}
